import { Toast } from '@tempmee/react';
import { useState } from 'react';
import { useLocation } from 'react-router';

/**
 * The key `SignUpForm` sets on the navigation state, exported so the two sides
 * of the hand-off cannot drift apart.
 */
export const REFERRAL_LINKED_STATE_KEY = 'referralLinked';

/**
 * GOTU-8354: a resolved code confirms itself to the office, the way mobile
 * does. It appears only when the lookup actually named a referrer — never for a
 * blank field, never for a failure (the inline error under the field covers
 * those), and never for `unavailable` or `server_error`, which allow sign-up
 * but confirmed nothing. Saying "linked" when the lookup never answered is a
 * guess.
 *
 * Deliberately says nothing about the bonus: what happened is that the code
 * resolved. The referral record itself is created later, server-side, from the
 * registration event.
 */
const REFERRAL_LINKED_MESSAGE = 'Referral linked successfully';
/** Mobile's `showReferralLinkedToast` uses 3000ms; the two confirmations match. */
const REFERRAL_TOAST_AUTO_HIDE_MS = 3000;
/**
 * Bottom-right, which was chosen when this lived on the split sign-up page: the
 * marketing panel took the left half and the form the right, so a confirmation
 * anchored bottom-left landed on the far side of a wide screen and was in fact
 * missed in review.
 *
 * That reason no longer applies here — the step pages are centred full-width
 * through `SignUpStepWrapper`, so neither corner is nearer the form. The
 * position is kept so the confirmation sits where it already sat rather than
 * moving for no reason; the DS default would be defensible now too.
 */
const REFERRAL_TOAST_ANCHOR = { vertical: 'bottom', horizontal: 'right' } as const;

/**
 * The "referral linked" confirmation, rendered by `SignUpLayout` rather than by
 * the form that earns it.
 *
 * A successful sign-up navigates from `/signup` to `/signup/your-info`, and
 * `SignUpPage` is a *sibling* of `SignUpLayout` in the route tree, not a child.
 * Opening a toast inside the form and navigating in the same handler unmounts
 * the thing rendering it, so the confirmation has to be hosted by something
 * that survives the hop. `SignUpLayout` wraps every post-account step, which
 * makes it the first such host.
 *
 * The open state is seeded from the navigation state **once, on mount**. Read
 * on every render it would reopen itself after the auto-hide, since the state
 * stays on the history entry; re-seeding it also means a browser Back onto this
 * entry cannot resurrect a confirmation for a sign-up completed minutes ago.
 */
export const ReferralLinkedToast = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(
    () => (location.state as Record<string, unknown> | null)?.[REFERRAL_LINKED_STATE_KEY] === true,
  );

  return (
    /*
      The dark bar — `severity="default"`, `bg-secondary` (#0D1F21) — not the
      green `success` one the ticket named. This mirrors mobile's
      `showReferralLinkedToast`, which deliberately uses the DS `dark` snackbar
      rather than the app's coloured toasts so that a sign-up confirmation looks
      like the other sign-up confirmations. The two clients' confirmations must
      read alike (GOTU-7317); the deviation from the ticket is recorded on the PR.
    */
    <Toast
      open={isOpen}
      message={REFERRAL_LINKED_MESSAGE}
      autoHideDuration={REFERRAL_TOAST_AUTO_HIDE_MS}
      anchorOrigin={REFERRAL_TOAST_ANCHOR}
      onClose={() => setIsOpen(false)}
    />
  );
};
